// Crop-dusting operations (#13): the farmer's own aircraft, assigned to the
// fields of a work order. A scout drone sweeps a field to confirm it's clear;
// then a crop-dusting helicopter sprays it in back-and-forth lanes plus an
// edge pass (#11), but only while the wind is inside the spray window. The
// hub-to-hub drones in sim.js are other farm traffic flying around them.
//
// Pure state and functions, no DOM.

import {
  FIELDS, HUBS, OPS_TYPES, SPRAY_WIND_MIN, SPRAY_WIND_MAX, CONFLICT_DIST, COLLISION_DIST, SCORING,
  WIRE_FIND_CHANCE, POWER, ENERGY_RESERVE,
} from '../config/constants.js';
import { dist } from './geometry.js';
import { incidentsEndShift } from './limits.js';
import { randomWorkOrder } from './freeplay.js';
import {
  stepAlerts, personInField, hubBlocked, chargeFactor, flockTracks,
} from './alerts.js';

const PARK_GAP = 0.07; // spacing between aircraft parked at the same pad (room for a callsign)
const PARK_DROP = 0.10; // parked below the pad, clear of its name label
// Live separation alone gives a fraction of a second between the ring and a
// collision when two aircraft meet head-on, so conflicts are also predicted:
// every airborne track is flown forward this far, in steps of LOOKAHEAD_STEP.
export const OPS_LOOKAHEAD = 10; // seconds
const LOOKAHEAD_STEP = 0.25;

export function opsType(id) {
  return OPS_TYPES.find((t) => t.id === id);
}

export function sprayWindowOpen(wind) {
  return wind.speed >= SPRAY_WIND_MIN && wind.speed <= SPRAY_WIND_MAX;
}

/**
 * New operations state for a shift: `fields` are FIELDS indices in the work
 * order, `aircraft` the type ids flying it (in hangar order).
 */
export function createOps({ fields = [0, 1, 2, 3], aircraft = ['scout', 'duster'] } = {}) {
  const counts = {};
  const perPad = {};
  const list = aircraft.map((typeId, i) => {
    const t = opsType(typeId);
    counts[typeId] = (counts[typeId] || 0) + 1;
    const slot = perPad[t.pad] = (perPad[t.pad] || 0) + 1;
    const hub = HUBS[t.pad];
    // park beside the pad, fanned out below it so labels don't collide
    const home = { x: hub.x + (slot - 1.5) * PARK_GAP, y: hub.y + PARK_DROP };
    return {
      id: `ops-${i + 1}`,
      type: t.id,
      call: `${t.prefix} ${counts[typeId]}`,
      color: t.color,
      padName: hub.name,  // its base's name
      hubIdx: t.pad,      // its base's hub, or null at a field pad
      homeHub: t.pad,
      base: `hub-${t.pad}`, // its landing zone (#33): a hub, or a field pad
      hubHome: { ...home },
      home,
      x: home.x,
      y: home.y,
      // parked | outbound | working | sprayHold | peopleHold | returning | padHold | recharging | holding
      state: 'parked',
      power: t.power,  // 'ev' or 'avgas' (#32)
      energy: 1,       // battery or fuel left, 0..1
      resume: null,    // state to go back to after a Hold
      task: null,      // { kind: 'scout' | 'spray', fieldIdx }
      path: null,
      idx: 0,          // index in `path` it's flying toward
      workFrom: 0,     // path indices where the field pattern starts and ends
      workTo: 0,
      holdAngle: 0,
      alert: false,    // inside the separation ring now
      predict: null,   // { with, in, at }: the soonest predicted conflict
    };
  });
  return {
    aircraft: list,
    fields: fields.map(newField),
    predicted: [],      // predictOpsConflicts, refreshed every tick
    scouted: 0,
    sprayed: 0,
    complete: false,
  };
}

// ---- patterns ------------------------------------------------------------

function inset(f, fx, fy) {
  return { x0: f.x + f.w * fx, x1: f.x + f.w * (1 - fx), y0: f.y + f.h * fy, y1: f.y + f.h * (1 - fy) };
}

/**
 * Back-and-forth lanes across field `f`, starting from the corner nearest
 * `from`. A scout flies a few wide passes; a duster many tight ones.
 */
export function lanePattern(f, lanes, from) {
  const r = inset(f, 0.1, 0.12);
  const startLeft = Math.abs(from.x - r.x0) <= Math.abs(from.x - r.x1);
  const startTop = Math.abs(from.y - r.y0) <= Math.abs(from.y - r.y1);
  const pts = [];
  for (let i = 0; i < lanes; i++) {
    const t = lanes === 1 ? 0.5 : i / (lanes - 1);
    const y = startTop ? r.y0 + (r.y1 - r.y0) * t : r.y1 - (r.y1 - r.y0) * t;
    const ltr = (i % 2 === 0) === startLeft;
    pts.push({ x: ltr ? r.x0 : r.x1, y }, { x: ltr ? r.x1 : r.x0, y });
  }
  return pts;
}

/** A duster's pattern: the lanes, then one edge (headland) pass around the field. */
export function sprayPattern(f, lanes, from) {
  const pts = lanePattern(f, lanes, from);
  const e = inset(f, 0.04, 0.05);
  const corners = [{ x: e.x0, y: e.y0 }, { x: e.x1, y: e.y0 }, { x: e.x1, y: e.y1 }, { x: e.x0, y: e.y1 }];
  const last = pts[pts.length - 1];
  let k = 0;
  corners.forEach((c, i) => { if (dist(c, last) < dist(corners[k], last)) k = i; });
  for (let i = 0; i <= 4; i++) pts.push(corners[(k + i) % 4]);
  return pts;
}

export function pathLength(pts) {
  let n = 0;
  for (let i = 1; i < pts.length; i++) n += dist(pts[i - 1], pts[i]);
  return n;
}

function patternFor(a, field) {
  const t = opsType(a.type);
  const f = FIELDS[field.idx];
  return t.task === 'scout' ? lanePattern(f, t.lanes, a) : sprayPattern(f, t.lanes, a);
}

// ---- assigning work -------------------------------------------------------

function statusLine(a, ops) {
  const f = a.task && ops.fields.find((x) => x.idx === a.task.fieldIdx);
  const verb = a.task?.kind === 'scout' ? 'scouting' : 'spraying';
  return f ? `${verb} ${f.name}` : a.state;
}

/**
 * What sending aircraft `aircraftId` to field `fieldIdx` would do, without
 * changing anything: { ok, label, reason?, note?, path? }.
 */
export function previewTask(ops, aircraftId, fieldIdx, wind) {
  const a = ops.aircraft.find((x) => x.id === aircraftId);
  const field = ops.fields.find((x) => x.idx === fieldIdx);
  if (!a || !field) return null;
  const t = opsType(a.type);
  const verb = t.task === 'scout' ? 'Scout' : 'Spray';
  const base = { aircraftId, fieldIdx, kind: t.task, label: `${verb} ${field.name}` };
  const no = (reason) => ({ ...base, ok: false, reason });

  if (!['parked', 'returning', 'padHold', 'recharging'].includes(a.state)) return no(`${a.call} is busy ${statusLine(a, ops)}`);
  if (wind.speed > t.maxWind) return no(`Too windy to fly: ${wind.speed} mph is over its ${t.maxWind} mph limit`);
  if (t.task === 'scout') {
    if (field.state !== 'unscouted') return no(field.state === 'scouting' ? 'Already being scouted' : 'Already scouted');
  } else {
    if (field.state === 'unscouted') return no('Not scouted yet: send a scout first');
    if (field.state === 'scouting') return no('Still being scouted');
    if (field.state === 'spraying') return no('Already being sprayed');
    if (field.state === 'sprayed') return no('Already sprayed');
  }
  const pattern = patternFor(a, field);
  const path = [{ x: a.x, y: a.y }, ...pattern, { ...a.home }];
  // enough battery or fuel for the whole trip, with some in hand (#32)
  const need = tripEnergy(a, path, pattern);
  if (need + ENERGY_RESERVE > a.energy) {
    const u = POWER[a.power].unit;
    return no(`Not enough ${u}: ${pct(a.energy)} left, the trip needs about ${pct(need + ENERGY_RESERVE)}`);
  }
  const notes = [];
  if (t.task === 'spray' && !sprayWindowOpen(wind)) {
    notes.push(`Wind ${wind.speed} mph: it will wait at the field edge until the wind is ${SPRAY_WIND_MIN}–${SPRAY_WIND_MAX} mph`);
  }
  // flight planning: would the new route meet one of your other aircraft?
  const ghost = { ...a, path, idx: 1, workFrom: 1, workTo: pattern.length, state: 'outbound' };
  const others = ops.aircraft.filter((x) => x.id !== a.id);
  const meet = predictConflicts([ghost], others, [], OPS_LOOKAHEAD * 2)[0];
  const crossing = meet ? { with: meet.b.call, in: meet.in, at: meet.at } : null;
  if (crossing) notes.push(`Meets ${crossing.with} in about ${Math.max(1, Math.round(crossing.in))}s: be ready to Hold one`);
  return { ...base, ok: true, path, pattern, note: notes.join(' · ') || null, crossing };
}

/** Send an aircraft to work a field. Returns the preview (check `ok`). */
export function assignTask(ops, aircraftId, fieldIdx, wind) {
  const pv = previewTask(ops, aircraftId, fieldIdx, wind);
  if (!pv || !pv.ok) return pv;
  const a = ops.aircraft.find((x) => x.id === aircraftId);
  const field = ops.fields.find((x) => x.idx === fieldIdx);
  a.task = { kind: pv.kind, fieldIdx };
  a.path = pv.path;
  a.idx = 1;
  a.workFrom = 1;
  a.workTo = pv.pattern.length; // path[workTo] is the pattern's last point
  a.state = 'outbound';
  const pass = { pattern: pv.pattern, progress: 0 };
  if (pv.kind === 'scout') { field.state = 'scouting'; field.sweep = pass; }
  else { field.state = 'spraying'; field.spray = pass; }
  return pv;
}

function abandonTask(ops, a) {
  if (!a.task) return;
  const field = ops.fields.find((x) => x.idx === a.task.fieldIdx);
  if (field && field.state === 'scouting') { field.state = 'unscouted'; field.sweep = null; }
  if (field && field.state === 'spraying') { field.state = 'clear'; field.spray = null; }
  a.task = null;
}

/** Fly an aircraft straight back to its pad, abandoning any unfinished field. */
export function returnToPad(ops, aircraftId) {
  const a = ops.aircraft.find((x) => x.id === aircraftId);
  if (!a || a.state === 'parked') return false;
  if (a.state !== 'returning') abandonTask(ops, a);
  a.path = [{ x: a.x, y: a.y }, { ...a.home }];
  a.idx = 1;
  a.workFrom = a.workTo = 0;
  a.state = 'returning';
  a.resume = null;
  return true;
}

function newField(idx) {
  return {
    idx,
    name: FIELDS[idx].name,
    state: 'unscouted', // unscouted | scouting | clear | spraying | sprayed
    sweep: null,        // { pattern, progress } of the last scout pass
    spray: null,        // { pattern, progress } of the last spray pass
  };
}

/**
 * A fresh work order on `fields` (free play, #46): aircraft working a field
 * head back to base; the counters keep counting.
 */
export function dealWorkOrder(ops, fields) {
  for (const a of ops.aircraft) if (a.task) returnToPad(ops, a.id);
  ops.fields = fields.map(newField);
  ops.complete = false;
}

/** Add one more aircraft of `typeId` to the hangar mid-shift (free play, #46). */
export function addAircraft(ops, typeId) {
  const t = opsType(typeId);
  if (!t) return null;
  const sameType = ops.aircraft.filter((a) => a.type === typeId).length;
  const atPad = ops.aircraft.filter((a) => a.homeHub === t.pad).length;
  const hub = HUBS[t.pad];
  const home = { x: hub.x + (atPad + 1 - 1.5) * PARK_GAP, y: hub.y + PARK_DROP };
  const a = {
    id: `ops-${ops.aircraft.length + 1}`,
    type: t.id,
    call: `${t.prefix} ${sameType + 1}`,
    color: t.color,
    padName: hub.name,
    hubIdx: t.pad,
    homeHub: t.pad,
    base: `hub-${t.pad}`,
    hubHome: { ...home },
    home,
    x: home.x,
    y: home.y,
    state: 'parked',
    power: t.power,
    energy: 1,
    resume: null,
    task: null,
    path: null,
    idx: 0,
    workFrom: 0,
    workTo: 0,
    holdAngle: 0,
    alert: false,
    predict: null,
  };
  ops.aircraft.push(a);
  return a;
}

/**
 * Make `zone` (a landing zone from zones.js) the aircraft's base and fly it
 * there, abandoning any unfinished field (#33). At its own hub it parks in
 * its usual spot; at the other hub, beside the hub's aircraft.
 */
export function landAt(ops, aircraftId, zone) {
  const a = ops.aircraft.find((x) => x.id === aircraftId);
  if (!a) return false;
  if (a.state !== 'returning') abandonTask(ops, a);
  a.base = zone.id;
  a.hubIdx = zone.hubIdx;
  a.padName = zone.label;
  if (zone.hubIdx === null) a.home = { x: zone.x, y: zone.y };
  else if (zone.hubIdx === a.homeHub) a.home = { ...a.hubHome };
  else a.home = { x: zone.x + PARK_GAP * 1.5, y: zone.y + PARK_DROP };
  a.path = [{ x: a.x, y: a.y }, { ...a.home }];
  a.idx = 1;
  a.workFrom = a.workTo = 0;
  a.state = 'returning';
  a.resume = null;
  return true;
}

/**
 * After a collision: the aircraft is set down at its pad straight away for
 * inspection (its field is abandoned), so it can't collide again next tick.
 */
export function landForInspection(ops, aircraftId) {
  const a = ops.aircraft.find((x) => x.id === aircraftId);
  if (!a) return false;
  abandonTask(ops, a);
  Object.assign(a, { x: a.home.x, y: a.home.y, state: 'parked', resume: null, path: null, idx: 0, workFrom: 0, workTo: 0, alert: false, predict: null });
  return true;
}

export function holdOps(ops, aircraftId) {
  const a = ops.aircraft.find((x) => x.id === aircraftId);
  if (!a || a.state === 'parked' || a.state === 'holding') return false;
  a.resume = a.state;
  a.state = 'holding';
  return true;
}

export function clearOps(ops, aircraftId) {
  const a = ops.aircraft.find((x) => x.id === aircraftId);
  if (!a || a.state !== 'holding') return false;
  a.state = a.resume || 'outbound';
  a.resume = null;
  return true;
}

export const isAirborne = (a) => a.state !== 'parked' && a.state !== 'recharging';

const pct = (x) => `${Math.round(x * 100)}%`;

/**
 * The share of a full battery or tank a trip along `path` uses (#32): the
 * pattern at working speed, the rest at cruise.
 */
export function tripEnergy(a, path, pattern = []) {
  const t = opsType(a.type);
  const work = pathLength(pattern);
  const transit = Math.max(0, pathLength(path) - work);
  return (transit / t.speed + work / t.workSpeed) / t.endurance;
}

// ---- predicting conflicts -------------------------------------------------

// Positions an aircraft of yours will pass through over the next `horizon`
// seconds, along its path at its speeds (a duster in a spray hold, and an
// aircraft holding, stay put). Index k is k+1 steps ahead.
function futureTrack(a, horizon) {
  const t = opsType(a.type);
  const n = Math.round(horizon / LOOKAHEAD_STEP);
  const pos = { x: a.x, y: a.y };
  let idx = a.idx;
  const moving = a.path && (a.state === 'outbound' || a.state === 'working' || a.state === 'returning');
  if (a.flock) return droneTrack(a, horizon);
  const pts = [];
  for (let k = 0; k < n; k++) {
    let budget = moving ? (inPattern(a, idx) ? t.workSpeed : t.speed) * LOOKAHEAD_STEP : 0;
    while (budget > 0 && idx < a.path.length) {
      const target = a.path[idx];
      const d = dist(pos, target);
      if (d <= budget) { pos.x = target.x; pos.y = target.y; budget -= d; idx += 1; }
      else { pos.x += ((target.x - pos.x) / d) * budget; pos.y += ((target.y - pos.y) / d) * budget; budget = 0; }
    }
    // back on the pad: off the board
    pts.push(moving && idx >= a.path.length ? null : { ...pos });
  }
  return pts;
}

// A hub drone flies straight to its destination; it's gone once there.
function droneTrack(d, horizon) {
  const n = Math.round(horizon / LOOKAHEAD_STEP);
  const pts = [];
  const total = dist(d, d.to);
  for (let k = 0; k < n; k++) {
    const travelled = d.speed * LOOKAHEAD_STEP * (k + 1);
    if (travelled >= total) { pts.push(null); continue; }
    pts.push({ x: d.x + ((d.to.x - d.x) / total) * travelled, y: d.y + ((d.to.y - d.y) / total) * travelled });
  }
  return pts;
}

/**
 * Pairs that will lose separation (come inside CONFLICT_DIST) within
 * `horizon` seconds, soonest first: { a, b, in: seconds, at: midpoint }.
 * `mine` against each other and against `others` (your other aircraft) and
 * `drones` (hub traffic). Holding and parked aircraft are left out, as in
 * opsSeparation.
 */
function predictConflicts(mine, others, drones, horizon = OPS_LOOKAHEAD) {
  const flying = (a) => isAirborne(a) && a.state !== 'holding';
  const tracks = new Map();
  const track = (x) => {
    if (!tracks.has(x)) tracks.set(x, x.to && !x.path ? droneTrack(x, horizon) : futureTrack(x, horizon));
    return tracks.get(x);
  };
  const pairs = [];
  const seen = new Set();
  const consider = (a, b) => {
    const key = [a.id, b.id].sort().join('|');
    if (seen.has(key)) return;
    seen.add(key);
    const ta = track(a);
    const tb = track(b);
    for (let k = 0; k < ta.length; k++) {
      if (!ta[k] || !tb[k]) continue;
      // a bird flock is a moving area: its radius widens the warning
      if (dist(ta[k], tb[k]) < CONFLICT_DIST + (a.r ?? 0) + (b.r ?? 0)) {
        pairs.push({ a, b, in: (k + 1) * LOOKAHEAD_STEP, at: { x: (ta[k].x + tb[k].x) / 2, y: (ta[k].y + tb[k].y) / 2 } });
        return;
      }
    }
  };
  const live = mine.filter(flying);
  for (let i = 0; i < live.length; i++) {
    for (let j = i + 1; j < live.length; j++) consider(live[i], live[j]);
    for (const b of others.filter(flying)) if (b !== live[i]) consider(live[i], b);
    for (const d of drones) if (d.state !== 'holding' && d.state !== 'landed') consider(live[i], d);
  }
  return pairs.sort((p, q) => p.in - q.in);
}

/**
 * Conflicts your airborne aircraft will run into within OPS_LOOKAHEAD
 * seconds (with each other or hub traffic), soonest first:
 * [{ a, b, in, at }], `a` always one of yours.
 */
export function predictOpsConflicts(ops, drones) {
  return predictConflicts(ops.aircraft, [], drones);
}

// ---- flying ---------------------------------------------------------------

function inPattern(a, idx = a.idx) {
  return a.workTo > 0 && idx > a.workFrom && idx <= a.workTo;
}

function patternProgress(a) {
  const pts = a.path.slice(a.workFrom, a.workTo + 1);
  const total = pathLength(pts);
  if (!total) return 1;
  let done = 0;
  for (let i = a.workFrom + 1; i < a.idx && i <= a.workTo; i++) done += dist(a.path[i - 1], a.path[i]);
  if (inPattern(a)) done += dist(a.path[a.idx - 1], a);
  return Math.min(1, done / total);
}

function finishPattern(ops, a, events) {
  const field = ops.fields.find((x) => x.idx === a.task.fieldIdx);
  const at = { x: a.x, y: a.y };
  if (a.task.kind === 'scout') {
    field.state = 'clear';
    field.sweep.progress = 1;
    ops.scouted += 1;
    events.push({ kind: 'scouted', fieldIdx: field.idx, field: field.name, call: a.call, ...at });
  } else {
    field.state = 'sprayed';
    field.spray.progress = 1;
    ops.sprayed += 1;
    events.push({ kind: 'sprayed', fieldIdx: field.idx, field: field.name, call: a.call, ...at });
  }
  a.task = null;
  a.state = 'returning';
}

/**
 * Advance every aircraft by `dt` seconds in `wind`. Returns events for the
 * UI and scoring: { kind: 'scouted' | 'sprayed', field, x, y }. `env`
 * (optional) brings in field and hub alerts (#31): peopleIn(fieldIdx) pauses
 * spraying there, padBlocked(hubIdx) keeps aircraft hovering over their
 * pad, and rechargeTime(hubIdx) is how long one recharges after landing.
 */
export function stepOps(ops, dt, wind, env = {}) {
  const events = [];
  const window = sprayWindowOpen(wind);
  for (const a of ops.aircraft) {
    if (a.state === 'parked') continue;
    if (a.state === 'recharging') {
      // charging or refueling at a base with its supply (#32)
      const rate = env.chargeRate?.(a) ?? 0;
      a.energy = Math.min(1, a.energy + rate * dt);
      if (a.energy >= 1 || rate <= 0) a.state = 'parked';
      continue;
    }
    // flying burns battery or fuel, holding and hovering included
    a.energy = Math.max(0, a.energy - dt / opsType(a.type).endurance);
    if (a.state === 'holding') { a.holdAngle += dt * 1.6; continue; }
    const t = opsType(a.type);
    // over its wind limit: abandon the field and head home
    if (wind.speed > t.maxWind && a.state !== 'returning') returnToPad(ops, a.id);
    let budget = (inPattern(a) ? t.workSpeed : t.speed) * dt;
    while (budget > 0 && a.path && a.idx < a.path.length) {
      // a duster only sprays inside the window: it waits at the field edge
      if (a.task?.kind === 'spray' && inPattern(a) && !window) { a.state = 'sprayHold'; break; }
      // nobody gets sprayed: it waits while a person is in the field (#31)
      if (a.task?.kind === 'spray' && inPattern(a) && env.peopleIn?.(a.task.fieldIdx)) { a.state = 'peopleHold'; break; }
      if (a.task && a.state !== 'returning') a.state = inPattern(a) ? 'working' : 'outbound';
      const target = a.path[a.idx];
      const d = dist(a, target);
      if (d <= budget) {
        a.x = target.x; a.y = target.y;
        budget -= d;
        if (a.task && a.idx === a.workTo) finishPattern(ops, a, events);
        a.idx += 1;
      } else {
        a.x += ((target.x - a.x) / d) * budget;
        a.y += ((target.y - a.y) / d) * budget;
        budget = 0;
      }
    }
    if (a.task) {
      const field = ops.fields.find((x) => x.idx === a.task.fieldIdx);
      const pass = a.task.kind === 'scout' ? field.sweep : field.spray;
      pass.progress = patternProgress(a);
    }
    if (a.path && a.idx >= a.path.length) {
      a.x = a.home.x; a.y = a.home.y;
      // a blocked pad: hover over it until it's clear (#31)
      if (env.padBlocked?.(a.hubIdx)) { a.state = 'padHold'; continue; }
      a.path = null; a.task = null; a.workFrom = a.workTo = 0;
      // refill if this base has its supply
      a.state = a.energy < 1 && (env.chargeRate?.(a) ?? 0) > 0 ? 'recharging' : 'parked';
    }
  }
  ops.complete = ops.fields.length > 0 && ops.fields.every((f) => f.state === 'sprayed');
  return events;
}

// ---- separation -----------------------------------------------------------

/**
 * Two aircraft came inside the separation ring: a near miss that wasn't
 * prevented, which costs points (#13). Counted once per pair.
 */
export function lostSeparation(s, a, b) {
  const key = [String(a.id), String(b.id)].sort().join('|');
  if (s.separatedPairs.has(key)) return;
  s.separatedPairs.add(key);
  s.separationLosses += 1;
}


/**
 * Live separation between the farmer's airborne aircraft and everything else
 * airborne (each other and the hub drones). Holding aircraft and drones are
 * clear of conflicts, as in sim.js. Returns { conflicts: Set of ids,
 * collisions: [{ a, b }] } with ids from both sides.
 */
export function opsSeparation(ops, drones) {
  const mine = ops.aircraft.filter((a) => isAirborne(a) && a.state !== 'holding');
  const others = drones.filter((d) => d.state !== 'holding' && d.state !== 'landed');
  const conflicts = new Set();
  const collisions = [];
  const pairs = []; // inside the ring, not colliding
  const check = (a, b) => {
    const d = dist(a, b);
    if (d < COLLISION_DIST) collisions.push({ a, b });
    else if (d < CONFLICT_DIST) { conflicts.add(a.id); conflicts.add(b.id); pairs.push([a, b]); }
  };
  for (let i = 0; i < mine.length; i++) {
    for (let j = i + 1; j < mine.length; j++) check(mine[i], mine[j]);
    for (const d of others) check(mine[i], d);
  }
  return { conflicts, collisions, pairs };
}

/**
 * One operations tick inside a shift: fly the farmer's aircraft in the
 * current wind, apply separation against the hub traffic, score finished
 * fields, and end the shift when the work order is done or on the last
 * incident. `windNow` is the wind for the current time of day.
 */
export function stepOperations(s, dt, windNow) {
  if (!s.ops || !s.running) return [];

  // field and hub alerts (#31)
  for (const e of stepAlerts(s, dt)) {
    if (e.kind === 'cleared' && e.handled) {
      // a supply truck reached a stranded aircraft (#32)
      if (e.alert.kind === 'stranded') {
        const craft = s.ops.aircraft.find((x) => x.id === e.alert.craftId);
        if (craft) craft.energy = 1;
      }
      s.hazardsHandled = (s.hazardsHandled ?? 0) + 1;
      s.rewards.push({ x: e.alert.x, y: e.alert.y, points: SCORING.hazardHandled, label: `${e.alert.kind === 'person' ? 'field' : 'pad'} cleared` });
    }
  }
  const env = {
    peopleIn: (fieldIdx) => personInField(s, fieldIdx),
    padBlocked: (hubIdx) => hubBlocked(s, hubIdx),
    // a base refills only the power it supplies (#32); field pads supply nothing
    chargeRate: (a) => {
      if (a.hubIdx === null || !HUBS[a.hubIdx].supplies.includes(a.power)) return 0;
      return chargeFactor(s, a.hubIdx, a.power) / opsType(a.type).refill;
    },
  };

  const events = stepOps(s.ops, dt, windNow, env);
  for (const e of events) {
    const points = e.kind === 'scouted' ? SCORING.fieldScouted : SCORING.fieldSprayed;
    s.rewards.push({ x: e.x, y: e.y, points, label: `${e.field} ${e.kind}` });
    // a scout pass can turn up a power line across the field
    if (e.kind === 'scouted' && s.alerts?.enabled && s.random() < WIRE_FIND_CHANCE) {
      const field = s.ops.fields.find((f) => f.idx === e.fieldIdx);
      if (field && !field.wire) {
        const f = FIELDS[field.idx];
        const y = f.y + f.h * (0.2 + s.random() * 0.6);
        field.wire = { from: { x: f.x, y }, to: { x: f.x + f.w, y: y + (s.random() - 0.5) * f.h * 0.3 } };
        s.alerts.notes.add('wire');
        s.hazardsHandled = (s.hazardsHandled ?? 0) + 1;
        s.rewards.push({ x: f.x + f.w / 2, y, points: SCORING.hazardHandled, label: 'power line found' });
      }
    }
  }
  birdStrikes(s);

  const { conflicts, collisions, pairs } = opsSeparation(s.ops, s.drones);
  for (const [a, b] of pairs) lostSeparation(s, a, b);
  for (const a of s.ops.aircraft) { a.alert = conflicts.has(a.id); a.predict = null; }
  // what's coming: each aircraft keeps its soonest predicted conflict
  s.ops.predicted = predictOpsConflicts(s.ops, [...s.drones, ...flockTracks(s)]);
  for (const p of s.ops.predicted) {
    for (const [x, y] of [[p.a, p.b], [p.b, p.a]]) {
      if (String(x.id).startsWith('ops-') && !x.predict) x.predict = { with: y.call, in: p.in, at: p.at };
    }
  }
  for (const d of s.drones) if (d.state !== 'holding' && conflicts.has(d.id)) d.state = 'conflict';

  if (collisions.length) {
    s.incidents += 1;
    for (const { a, b } of collisions) {
      for (const x of [a, b]) {
        if (String(x.id).startsWith('ops-')) landForInspection(s.ops, x.id);
        else s.drones = s.drones.filter((d) => d !== x);
      }
    }
    if (incidentsEndShift(s)) s.running = false;
  }
  if (s.ops.complete) {
    // free play (#46) deals a new work order instead of ending
    if (s.free) {
      s.rewards.push({ x: 0.5, y: 0.5, points: 0, label: 'work order done: a new one is in' });
      dealWorkOrder(s.ops, randomWorkOrder(s.random));
    } else s.running = false;
  }
  return events;
}

/**
 * Bird flocks (#31): your aircraft that fly into one land for inspection;
 * a hub drone that does is lost. Holding aircraft circle clear of them.
 * Anything close to a flock shows its warning ring.
 */
function birdStrikes(s) {
  for (const flock of flockTracks(s)) {
    for (const a of s.ops.aircraft) {
      if (!isAirborne(a) || a.state === 'holding') continue;
      const d = dist(a, flock);
      if (d < flock.r) {
        landForInspection(s.ops, a.id);
        s.birdStrikes = (s.birdStrikes ?? 0) + 1;
        s.rewards.push({ x: flock.x, y: flock.y, points: SCORING.birdStrike, label: `bird strike: ${a.call} grounded` });
      }
    }
    for (const d of s.drones) {
      if (d.state === 'holding' || d.state === 'landed') continue;
      const gap = dist(d, flock);
      if (gap < flock.r) {
        d.birdStruck = true;
        s.birdStrikes = (s.birdStrikes ?? 0) + 1;
        s.rewards.push({ x: flock.x, y: flock.y, points: SCORING.birdStrike, label: `bird strike: ${d.call} lost` });
      } else if (gap < flock.r + CONFLICT_DIST) d.state = 'conflict';
    }
    s.drones = s.drones.filter((d) => !d.birdStruck);
  }
}
